import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCheck, faFileCircleXmark, faHourglass, faWandMagicSparkles } from '@fortawesome/pro-light-svg-icons';
import type {
  CADJobStatusOptions,
  ModelStatus,
  PartCategoriesStatusOptions,
  ProductListResult,
  ProductStatus,
} from '@partful/shared-types';
import type { CADUploadJob } from '@providers/job/types';
import { makeProduct } from '@services/testExamples';

type InstructionStatus = CADJobStatusOptions | PartCategoriesStatusOptions | ModelStatus | 'UNKNOWN';
type InstructionSimpleStatus =
  | 'COMPLETED'
  | 'DRAFT'
  | 'DELETED'
  | 'CANCELLED'
  | 'CREATE_IN_PROGRESS'
  | 'PROCESSING'
  | 'FAILED'
  | 'UNKNOWN';

type CategorisedProducts = {
  publishedProducts: ProductListResult[];
  draftProducts: ProductListResult[];
  inProgressProducts: ProductListResult[];
  processingProducts: ProductListResult[];
  failedProducts: ProductListResult[];
  otherProducts: ProductListResult[];
};

type InstructionCardDetails = {
  status: InstructionStatus;
  simpleStatus: InstructionSimpleStatus;
  icon: IconDefinition;
  destination: string;
};

/**
 * Returns a single unified status for the product from the various status values available
 */
export function getProductStatus({ status }: ProductListResult): InstructionStatus {
  if (status?.cadJob && status.cadJob !== 'COMPLETED') {
    return status.cadJob;
  }
  if (status?.partCategoriesJob && !status.partCategoriesJob.startsWith('COMPLETED')) {
    return status.partCategoriesJob;
  }

  return status?.model ?? 'UNKNOWN';
}

/**
 * Returns a href where a given product card should navigate to when clicked
 */
export function getProductCardDestination(product: ProductListResult, status: InstructionStatus): string {
  switch (status) {
    case 'IN_PROGRESS_REQUIRES_CAD_FILE': {
      return `/instructions/create/details?runID=${product.runID}`;
    }
    case 'IN_PROGRESS_REQUIRES_GENERAL_ASSEMBLY': {
      return `/instructions/create/general-assembly?runID=${product.runID}&productName=${product.name}`;
    }
    case 'IN_PROGRESS_PROCESSING_CAD':
    case 'IN_PROGRESS_CAD_PROCESSING_FAILED': {
      return `/instructions/create/processing?runID=${product.runID}&productName=${product.name}`;
    }
    case 'IN_PROGRESS_REQUIRES_SALES_BOM':
    case 'IN_PROGRESS_REQUIRES_ORIENTATION_MAPPING':
    case 'IN_PROGRESS_REQUIRES_ATTRIBUTE_MAPPING':
    case 'COMPLETED':
    case 'DRAFT': {
      return `/instructions/viewer/${product.productExternalID}`;
    }
    default: {
      return '#';
    }
  }
}

/**
 * Returns a simplied, more high-level status value based on a more detailed status
 */
export function getProductSimpleStatus(status: InstructionStatus): InstructionSimpleStatus {
  switch (status) {
    case 'COMPLETED':
    case 'COMPLETED_READY_FOR_PREVIEW': {
      return 'COMPLETED';
    }
    case 'DRAFT':
    case 'IN_PROGRESS_REQUIRES_SALES_BOM':
    case 'IN_PROGRESS_REQUIRES_ORIENTATION_MAPPING':
    case 'IN_PROGRESS_REQUIRES_ATTRIBUTE_MAPPING': {
      return 'DRAFT';
    }
    case 'IN_PROGRESS':
    case 'IN_PROGRESS_3D_CURATION':
    case 'IN_PROGRESS_INDEXING':
    case 'IN_PROGRESS_INGEST':
    case 'IN_PROGRESS_PROCESSING_CAD':
    case 'IN_PROGRESS_UPLOAD':
    case 'AWAITING_OPTIMISATION': {
      return 'PROCESSING';
    }
    case 'IN_PROGRESS_REQUIRES_CAD_FILE':
    case 'IN_PROGRESS_REQUIRES_GENERAL_ASSEMBLY': {
      return 'CREATE_IN_PROGRESS';
    }
    case 'FAILED':
    case 'IN_PROGRESS_CAD_PROCESSING_FAILED': {
      return 'FAILED';
    }
    case 'DELETED': {
      return 'DELETED';
    }
    case 'CANCELLED_BY_USER': {
      return 'CANCELLED';
    }
    default: {
      return 'UNKNOWN';
    }
  }
}

/**
 * Return an appropriate icon for a product based on it's current status
 */
export function getProductStatusIcon(status: InstructionSimpleStatus): IconDefinition {
  switch (status) {
    case 'COMPLETED':
    case 'DRAFT': {
      return faCheck;
    }
    case 'PROCESSING': {
      return faHourglass;
    }
    case 'CREATE_IN_PROGRESS': {
      return faWandMagicSparkles;
    }
    default: {
      return faFileCircleXmark;
    }
  }
}

/**
 * Return values useful to render a product card such as statuses
 */
export function calculateProductCardDetails(product: ProductListResult): InstructionCardDetails {
  const status = getProductStatus(product);
  const simpleStatus = getProductSimpleStatus(status);
  const icon = getProductStatusIcon(simpleStatus);
  const destination = getProductCardDestination(product, status);

  return {
    status,
    simpleStatus,
    icon,
    destination,
  };
}

export function categoriseCADJobByStatus(jobs: CADUploadJob[]): CategorisedProducts {
  return jobs.reduce(
    (out, job) => {
      const instructionStatus = job.status;
      const simpleStatus = getProductSimpleStatus(instructionStatus);

      switch (simpleStatus) {
        case 'CREATE_IN_PROGRESS': {
          out.inProgressProducts.push(jobToProductListResult(job));
          break;
        }
        case 'PROCESSING': {
          out.processingProducts.push(jobToProductListResult(job));
          break;
        }
        case 'DRAFT': {
          out.draftProducts.push(jobToProductListResult(job));
          break;
        }
        case 'FAILED': {
          out.failedProducts.push(jobToProductListResult(job));
          break;
        }
        default: {
          out.otherProducts.push(jobToProductListResult(job));
          break;
        }
      }

      return out;
    },
    {
      publishedProducts: [],
      draftProducts: [],
      inProgressProducts: [],
      processingProducts: [],
      failedProducts: [],
      otherProducts: [],
    } as CategorisedProducts
  );
}

function jobToProductListResult(job: CADUploadJob): ProductListResult {
  const modelStatus = jobStatusToModelStatus(job.status);
  const status = jobToStatus(job);
  return {
    ...job,
    productExternalID: job.productName,
    name: job.productName,
    // following props are required in ProductListResult but not used. They are set
    // here to recreate the behaviour in GET /configurations
    thumbnailUrl: '',
    createdAt: '',
    updatedAt: '',
    customerRef: '',
    updatedBy: 'unknown', // matches value returned by old GET /configurations endpoint
    productId: '',
    createdById: '',
    modelStatus,
    status,
  };
}
// This was copied from the old GET /configurations implementation
const jobStatusToModelStatus = (status: CADJobStatusOptions): ModelStatus => {
  if (status === 'FAILED') return 'DELETED';
  if (status === 'COMPLETED') return 'COMPLETED';
  return 'DRAFT';
};

// Recreates the `status` property that GET /configurations used to create for CADJobs
const jobToStatus = (job: CADUploadJob): ProductStatus => ({
  model: jobStatusToModelStatus(job.status),
  partTree: 'INCOMPLETE',
  cadJob: job.status === 'FAILED' ? 'IN_PROGRESS_CAD_PROCESSING_FAILED' : job.status,
});

/**
 * Returns array of products for various statuses
 */
export function categoriseProductsByStatus(products: ProductListResult[]): CategorisedProducts {
  return products.reduce(
    (out, product) => {
      const status = getProductStatus(product);
      const simpleStatus = getProductSimpleStatus(status);

      switch (simpleStatus) {
        case 'COMPLETED': {
          out.publishedProducts.push(product);
          break;
        }
        case 'DRAFT': {
          out.draftProducts.push(product);
          break;
        }
        /* istanbul ignore next default required by linter, should never happen */
        default: {
          out.otherProducts.push(product);
          break;
        }
      }

      return out;
    },
    {
      publishedProducts: [],
      draftProducts: [],
      inProgressProducts: [],
      processingProducts: [],
      failedProducts: [],
      otherProducts: [],
    } as CategorisedProducts
  );
}

/**
 * Returns a product defintion useful for testing
 */
/* istanbul ignore next - testing util only */
export function getTestProduct({
  cadJob = 'COMPLETED',
  partTree = 'COMPLETED',
  model = 'COMPLETED',
  partCategoriesJob = 'COMPLETED',
  name,
}: Partial<ProductStatus & { name: string }> = {}): ProductListResult {
  return {
    ...makeProduct(),
    ...(name ? { name } : {}),
    thumbnailUrl:
      'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAIAAAD8GO2jAAAAiElEQVR4nGKRX8TEgA28fPYLq3jK5Gis4udafmMVx246FcGoBaMWUA4YowuysEpE+3/FKt72Zi1W8UjFXqziQz+IRi0YARYw3vuyAKvEpMyVWMXj1uRjFd+eyI1VfOgH0agFI8ACFhONf9hlns7CKly1nwWr+O2kIKziQz+IRi0YARYAAgAA///rPRm/moVlDAAAAABJRU5ErkJggg==',
    status: {
      cadJob,
      model,
      partTree,
      partCategoriesJob,
    },
  };
}
